import { type DocumentType, modelOptions, post, pre, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { CropConfigurations, isCropType } from "../../../Configurations/CropConfiguration/CropConfigurations.js";
import { getCropVarietyConfiguration } from "../../../Configurations/CropConfiguration/CropVarietyConfigurations.js";
import { CropTypeString, CropVarietyKeyString, getZodSchemaFromClass, validate } from "../../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../schemaBase.js";
import { ZoneMetadataFieldsWithDefaults, ZoneMetadataSchemaTypes } from "../../types/zoneMetadata.d.js";
import { DtoValidatorKeys, DtoValidatorTypes } from "../../validatableClass.js";

const SCHEMA_KEY = "ZoneMetadata" as const;

/**
 * Matches Zod's `.positive()` on the planting lengths. Mongoose's `min` is inclusive, so `min: 0` would
 * store a 0 the DTO refuses. Not run on an unset value, which is how a cleared length is stored.
 */
const GREATER_THAN_ZERO = {
    validator: (value: number): boolean => value > 0,
    message: "{PATH} must be greater than 0",
};

type CropIssueField = "cropVariety" | "customCropName" | "customCropVarietyName";

/** A partial DTO may omit fields, so with `partial` an absent value contradicts nothing. */
function cropFieldIssues(
    fields: Pick<ZoneMetadata, "cropType" | CropIssueField>,
    partial = false,
): { field: CropIssueField; message: string }[] {
    const issues: { field: CropIssueField; message: string }[] = [];
    const crop = fields.cropType && isCropType(fields.cropType) ? CropConfigurations[fields.cropType] : undefined;
    const variety = fields.cropVariety ? getCropVarietyConfiguration(fields.cropVariety) : undefined;
    const absent = (value: string | undefined) => !value && !(partial && value === undefined);

    if (variety && variety.crop !== fields.cropType && !(partial && fields.cropType === undefined)) {
        issues.push({ field: "cropVariety", message: "Crop variety doesn't belong to the zone's crop" });
    }
    if (crop?.customName && absent(fields.customCropName)) {
        issues.push({ field: "customCropName", message: "A custom crop needs its name" });
    }
    if (variety?.customName && absent(fields.customCropVarietyName)) {
        issues.push({ field: "customCropVarietyName", message: "A custom variety needs its name" });
    }
    return issues;
}

// v1 saves skip the DTO.
function invalidateCropFields(this: DocumentType<ZoneMetadata>) {
    if (this.cropVariety && !getCropVarietyConfiguration(this.cropVariety)) {
        this.invalidate("cropVariety", "Unknown crop variety", this.cropVariety);
    }
    cropFieldIssues(this).forEach(({ field, message }) => this.invalidate(field, message, this[field]));
}

@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@pre<ZoneMetadata>("validate", invalidateCropFields)
@post<SchemaBase<ZoneMetadata>>(...addSchemaKeyHook(SCHEMA_KEY))
export class ZoneMetadata extends SchemaBase<ZoneMetadata, ZoneMetadataFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public varietal?: string;

    /** A `CropConfigurations` key. */
    @validate({ zodValidator: CropTypeString.optional() })
    @prop({ type: () => String, required: false })
    public cropType?: string;

    /** A `CropVarietyConfigurations` key of the zone's crop. */
    @validate({ zodValidator: CropVarietyKeyString.optional() })
    @prop({ type: () => String, required: false })
    public cropVariety?: string;

    /** The zone's own crop name, for a crop whose config has `customName`. */
    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public customCropName?: string;

    /** The zone's own variety name, for a variety whose config has `customName`. */
    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public customCropVarietyName?: string;

    /** Planting geometry in metres. These are independent of irrigation emitter spacing. */
    @validate({ zodValidator: z.number().positive().optional() })
    @prop({ type: () => Number, required: false, validate: GREATER_THAN_ZERO })
    public rowSpacing?: number;

    /** Preferred display unit for row spacing; the measured value remains in metres. */
    @validate({ zodValidator: z.enum(["meter", "foot", "inch"]).optional() })
    @prop({ type: () => String, required: false })
    public rowSpacingDisplayUnit?: "meter" | "foot" | "inch";

    @validate({ zodValidator: z.number().positive().optional() })
    @prop({ type: () => Number, required: false, validate: GREATER_THAN_ZERO })
    public plantSpacing?: number;

    /** Preferred display unit for plant spacing; independent of the row spacing unit. */
    @validate({ zodValidator: z.enum(["meter", "foot", "inch"]).optional() })
    @prop({ type: () => String, required: false })
    public plantSpacingDisplayUnit?: "meter" | "foot" | "inch";

    /** Typical row length in metres, for counting plants by row rather than by area. */
    @validate({ zodValidator: z.number().positive().optional() })
    @prop({ type: () => Number, required: false, validate: GREATER_THAN_ZERO })
    public rowLength?: number;

    /** Preferred display unit for row length; the measured value remains in metres. */
    @validate({ zodValidator: z.enum(["meter", "foot", "inch"]).optional() })
    @prop({ type: () => String, required: false })
    public rowLengthDisplayUnit?: "meter" | "foot" | "inch";

    /** Number of planted rows in the zone. */
    @validate({ zodValidator: z.number().int().positive().optional() })
    @prop({ type: () => Number, required: false, min: 1 })
    public rowCount?: number;

    /** Rootstock name, independent of the legacy rootClone field. */
    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public rootstock?: string;

    /** Clonal selection within a cultivar: clone for grapes, strain or sport for tree fruit. */
    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public clonalSelection?: string;

    @validate({ zodValidator: z.coerce.date().optional() })
    @prop({ type: () => Date, required: false })
    public datePlanted?: Date;

    /**
     * in meters
     * @type {number}
     */
    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: false })
    public emitterSpacing?: number;

    /**
     * in gallons per hour
     * @type {number}
     */
    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: false })
    public emitterFlowRate?: number;

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public rootClone?: string;

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public vineClone?: string;

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public notes?: string;

    /** The pressure the zone needs, as metres of water head. Nothing reads it yet. */
    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: false })
    public zonePressureHead?: number;

    /**
     * The whole zone's flow while irrigating, in litres per hour. The irrigation report multiplies
     * irrigated hours by it for its theoretical volumes.
     */
    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: false })
    public totalZoneFlowRate?: number;

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public connectedPump?: string;

    static dtoValidators: DtoValidatorTypes<ZoneMetadataSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<ZoneMetadata, ZoneMetadataFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

ZoneMetadata.dtoValidators = getZodSchemaFromClass<ZoneMetadata>(ZoneMetadata, {
    superRefine: (metadata, ctx, variant) => {
        cropFieldIssues(metadata, variant === DtoValidatorKeys.Update).forEach(({ field, message }) => {
            ctx.addIssue({ code: "custom", path: [field], message });
        });
    },
}) as unknown as DtoValidatorTypes<ZoneMetadataSchemaTypes>;
